import * as Sentry from '@sentry/tanstackstart-react'
import mixpanel from 'mixpanel-browser'
import { ANALYTICS_ENVIRONMENTS, getEventVersion } from './analytics-contracts'
import { isMixpanelInitialized } from './mixpanel'

// ---------------------------------------------------------------------------
// Canonical event union — minimal product set. The funnel events
// (checkout_*, contact_submitted, plan_selected, cta_clicked, scroll_depth,
// pricing_viewed, schools_viewed) live on the lander, not here. Backend emits
// the truth events for product activity (rule_created, account_created, etc.)
// via emitServerEvent — see backend/src/services/analytics.ts.
// ---------------------------------------------------------------------------

export type TrackEvent =
  | { name: 'page_viewed'; props: { page: string } }
  | { name: 'logged_in'; props?: Record<string, never> }

// ---------------------------------------------------------------------------
// Destination mapping table
// ---------------------------------------------------------------------------

type Destinations = {
  mixpanel: string | null
}

const EVENT_DESTINATIONS: Record<TrackEvent['name'], Destinations> = {
  page_viewed: { mixpanel: 'Page Viewed' },
  logged_in: { mixpanel: 'Logged In' },
}

function getAnalyticsEnvironment(): string {
  return (
    import.meta.env.VITE_ANALYTICS_ENVIRONMENT ||
    import.meta.env.VITE_SENTRY_ENVIRONMENT ||
    import.meta.env.MODE
  )
}

function shouldDispatchAnalytics(): boolean {
  const analyticsEnvironment = getAnalyticsEnvironment()
  return (
    analyticsEnvironment === ANALYTICS_ENVIRONMENTS.PRODUCTION ||
    analyticsEnvironment === ANALYTICS_ENVIRONMENTS.STAGING
  )
}

// ---------------------------------------------------------------------------
// Adapters
// ---------------------------------------------------------------------------

function mixpanelAdapter(event: TrackEvent, mpName: string): void {
  const analyticsEnvironment = getAnalyticsEnvironment()
  mixpanel.track(mpName, {
    environment: analyticsEnvironment,
    event_name: event.name,
    event_version: getEventVersion(event.name),
    ...('props' in event && event.props ? event.props : {}),
  })
}

export function track(event: TrackEvent): void {
  if (!shouldDispatchAnalytics()) {
    console.log('[track]', event.name, 'props' in event ? event.props : undefined)
    return
  }

  const destinations = EVENT_DESTINATIONS[event.name]

  if (destinations.mixpanel !== null && isMixpanelInitialized()) {
    const destination = 'mixpanel'
    try {
      mixpanelAdapter(event, destinations.mixpanel)
    } catch (err) {
      Sentry.captureException(err, { tags: { service: 'track', event: event.name, destination } })
    }
  }
}
